import * as THREE from 'three';
import type { Opening } from '../../core/hp/types';
import { openingDesign, openingKind } from '../../state/openingProperties';

export interface OpeningFrame {
  center: { x: number; z: number }; centerY: number; width: number; height: number; yaw: number; thickness: number;
}

export function openingAssembly(opening: Opening, frame: OpeningFrame): THREE.Group {
  const holder = new THREE.Group();
  holder.position.set(frame.center.x, opening.sill, frame.center.z);
  holder.rotation.y = frame.yaw;
  holder.name = `opening:${opening.id}`;
  holder.userData = { kind: 'opening', openingId: opening.id, caption: opening.caption };
  return holder;
}

function box(parent: THREE.Object3D, material: THREE.Material, size: [number, number, number], at: [number, number, number], name?: string) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material);
  mesh.position.set(...at); mesh.name = name ?? ''; mesh.castShadow = true; mesh.receiveShadow = true;
  parent.add(mesh); return mesh;
}

/** Drapery attaches to the room side of the wall, independently of the leaf orientation. */
export function addOpeningCurtains(holder: THREE.Object3D, opening: Opening, frame: OpeningFrame): void {
  const kind = openingDesign(opening, frame.thickness).curtains;
  if (kind === 'none' || openingKind(opening) !== 'window') return;
  const curtains = new THREE.Group(); curtains.name = 'curtains';
  curtains.position.z = frame.thickness / 2 + .11; holder.add(curtains);
  const fabric = new THREE.MeshStandardMaterial({ color: kind === 'drapes' ? '#b1a697' : '#f4f1e9', roughness: 1,
    side: THREE.DoubleSide, transparent: kind === 'sheer', opacity: kind === 'sheer' ? .38 : 1, depthWrite: kind !== 'sheer' });
  const rail = new THREE.MeshStandardMaterial({ color: '#77716b', metalness: .5, roughness: .45 });
  box(curtains, rail, [opening.width + .3, .035, .04], [0, opening.height + .1, 0]);
  if (kind === 'roller') {
    box(curtains, fabric, [opening.width + .04, opening.height * .58, .018], [0, opening.height * .71, .025], 'roller');
    box(curtains, rail, [opening.width + .08, .028, .028], [0, opening.height * .42, .025]);
    return;
  }
  const bottom = kind === 'drapes' ? -opening.sill + .04 : -.08;
  const top = opening.height + .09, height = top - bottom;
  for (const side of [-1, 1]) {
    const width = opening.width * (kind === 'sheer' ? .53 : .29) + .09;
    const center = side * (opening.width / 2 + .08 - width / 2);
    const geometry = new THREE.PlaneGeometry(width, height, 36, 1);
    const positions = geometry.getAttribute('position');
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      positions.setZ(i, .026 * Math.cos((x + width / 2) / width * Math.PI * 14));
    }
    geometry.computeVertexNormals();
    const panel = new THREE.Mesh(geometry, fabric); panel.position.set(center, (top + bottom) / 2, .04);
    panel.name = side < 0 ? 'curtain:left' : 'curtain:right'; panel.castShadow = kind !== 'sheer'; panel.receiveShadow = true;
    curtains.add(panel);
  }
}

export function buildProceduralOpening(opening: Opening, frame: OpeningFrame): THREE.Group | null {
  const design = openingDesign(opening, frame.thickness), kind = openingKind(opening);
  if (!design.style || (kind !== 'door' && kind !== 'window')) return null;
  const holder = openingAssembly(opening, frame), aperture = new THREE.Group();
  aperture.name = 'aperture'; aperture.position.z = frame.thickness / 2 - design.recess;
  aperture.rotation.y = opening.flipped ? Math.PI : 0; holder.add(aperture);
  const paint = new THREE.MeshStandardMaterial({ color: '#e6e3dc', roughness: .48 });
  const leafPaint = new THREE.MeshStandardMaterial({ color: kind === 'door' ? '#d2c1aa' : '#eeeae2', roughness: .58 });
  const metal = new THREE.MeshStandardMaterial({ color: '#64635f', metalness: .7, roughness: .3 });
  const glass = new THREE.MeshPhysicalMaterial({ color: '#c7e0df', roughness: .09, metalness: 0,
    transparent: true, opacity: .26, depthWrite: false, side: THREE.DoubleSide });
  const width = opening.width, height = opening.height, profile = Math.min(.06, width / 9, height / 12), depth = Math.min(.085, frame.thickness);
  const fixed = new THREE.Group(); fixed.name = 'frame'; aperture.add(fixed);
  box(fixed, paint, [profile, height, depth], [-width / 2 + profile / 2, height / 2, 0]);
  box(fixed, paint, [profile, height, depth], [width / 2 - profile / 2, height / 2, 0]);
  box(fixed, paint, [width - 2 * profile, profile, depth], [0, height - profile / 2, 0]);
  if (kind === 'window') box(fixed, paint, [width - 2 * profile, profile, depth], [0, profile / 2, 0]);
  const count = design.style === 'triple' ? 3 : design.style === 'double' || design.style === 'double-door' ? 2 : 1;
  const innerWidth = width - 2 * profile, leafWidth = innerWidth / count;
  const bottom = kind === 'window' ? profile : Math.min(.012, height * .04), leafHeight = height - profile - bottom;
  for (let i = 0; i < count; i++) {
    const sign = i === count - 1 && count > 1 ? -1 : 1;
    const center = -innerWidth / 2 + leafWidth * (i + .5);
    const hinge = new THREE.Group(); hinge.name = `hinge:${i}`;
    hinge.position.set(center - sign * leafWidth / 2, bottom, 0);
    hinge.rotation.y = -sign * design.openAngle * Math.PI / 180; aperture.add(hinge);
    const leaf = new THREE.Group(); leaf.name = `leaf:${i}`; leaf.position.x = sign * leafWidth / 2; hinge.add(leaf);
    const sash = Math.min(profile * .65, leafWidth / 8), thickness = .038;
    if (kind === 'door' && design.style !== 'glazed') {
      box(leaf, leafPaint, [leafWidth - Math.min(.006, leafWidth * .02), leafHeight, thickness], [0, leafHeight / 2, 0]);
      box(leaf, paint, [leafWidth * .67, leafHeight * .63, .009], [0, leafHeight * .57, thickness / 2 + .004]);
    } else {
      box(leaf, leafPaint, [sash, leafHeight, thickness], [-leafWidth / 2 + sash / 2, leafHeight / 2, 0]);
      box(leaf, leafPaint, [sash, leafHeight, thickness], [leafWidth / 2 - sash / 2, leafHeight / 2, 0]);
      box(leaf, leafPaint, [leafWidth - 2 * sash, sash, thickness], [0, sash / 2, 0]);
      box(leaf, leafPaint, [leafWidth - 2 * sash, sash, thickness], [0, leafHeight - sash / 2, 0]);
      const pane = box(leaf, glass, [leafWidth - 2 * sash, leafHeight - 2 * sash, .009], [0, leafHeight / 2, 0], `glass:${i}`);
      pane.castShadow = false;
      if (kind === 'door') box(leaf, leafPaint, [leafWidth - 2 * sash, leafHeight * .32, thickness], [0, leafHeight * .17, 0]);
    }
    box(leaf, metal, [.023, .115, .024], [sign * (leafWidth / 2 - sash * 1.5), leafHeight * .48, .035], `handle:${i}`);
  }
  addOpeningCurtains(holder, opening, frame);
  return holder;
}
